import React from 'react'

import { imageAsFullImageSrc } from '@scoir/avatar-image-url'
import { HeightTracker } from '@scoir/college-details'
import { HeightObserver } from '@scoir/height-observer'
import { clearSignedInApp } from '@scoir/redirects'
import window from 'browser-lib/window'
import { filter, get, head, includes, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { NavLink } from 'react-router-dom'

import api from 'common/api'
import { removeBearerToken } from 'common/bearer'
import collegeFeatures from 'common/college-features'
import features from 'common/college-features'
import userPermissions from 'common/system-permissions'
import useCollegeName from 'common/use-college-name'
import userPilotHelper from 'common/user-pilot-helper'
import withContext from 'components/RealtimeMessagingProvider/withContext'
import { isFeatureEnabled } from 'store/college'
import { isHandraisersEnabled } from 'store/feature-toggle'
import { actions as mapFilterActions } from 'store/map-filters'

import DirectMessaging from './DirectMessaging'
import HeaderNavigation from './PageFrameHeaderNavigation'

const onlyIfSubItemsExist = (config) => !isEmpty(config.subItems) && config

export const TOP_NAVIGATION_HEIGHT_ID = 'top-navigation'

export class AdmissionsHeaderNavigation extends React.Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    scid: PropTypes.number,
    updateFilters: PropTypes.func.isRequired,
    documentDelegateName: PropTypes.string,
    realtimeConnect: PropTypes.func.isRequired,
    realtimeLogout: PropTypes.func.isRequired,
    collegeName: PropTypes.string.isRequired,
    supportsCrmIntegration: PropTypes.bool.isRequired,
    applicationIntegrationsEnabled: PropTypes.bool.isRequired,
    applicationActivationEnabled: PropTypes.bool.isRequired,
    scoirApplyEnabled: PropTypes.bool.isRequired,
    isShadow: PropTypes.bool.isRequired,
    supportsHandraisers: PropTypes.bool.isRequired,
  }
  hasPermission(key) {
    return includes(this.props.user.Permissions, userPermissions[key])
  }
  render() {
    const {
      realtimeConnect,
      realtimeLogout,
      user,
      scid,
      documentDelegateName,
      updateFilters,
      collegeName,
      supportsCrmIntegration,
      applicationIntegrationsEnabled,
      applicationActivationEnabled,
      scoirApplyEnabled,
      isShadow,
      handraisersEnabled,
      supportsHandraisers,
    } = this.props
    const ROOT = import.meta.env.BASE_URL
    const canManage = this.hasPermission('MANAGE_ADMISSIONS')
    const canViewApplicants = this.hasPermission('VIEW_APPLICANTS')
    const canManageApplicationRounds = this.hasPermission('MANAGE_COLLEGE_APPLICATION_ROUNDS')
    const canRecruit = this.hasPermission('PERFORM_VISIT_MANGEMENT_TASKS')
    const canAdmissions = this.hasPermission('PERFORM_ADMISSIONS_TASKS')
    const hasConfigureApplicationPermissions =
      this.hasPermission('TEST_COLLEGE_APPLICATION') ||
      this.hasPermission('PERFORM_ADMISSIONS_TASKS') ||
      this.hasPermission('MANAGE_COLLEGE_APPLICATION_ROUNDS') ||
      this.hasPermission('PERFORM_UNSPECIFIED_ADMIN_TASKS') ||
      isShadow
    const canConfigureApplicationIntegration = applicationIntegrationsEnabled && hasConfigureApplicationPermissions
    const canActivateApplications = applicationActivationEnabled && hasConfigureApplicationPermissions
    const canViewAppExtensions = (scoirApplyEnabled && canManageApplicationRounds) || isShadow
    const canViewHandraisers = supportsHandraisers && this.hasPermission('VIEW_STUDENT_INTERESTS')
    const canConfigureSFTP = this.hasPermission('CONFIGURE_INTEGRATIONS') && !documentDelegateName
    const canManageVisits = this.hasPermission('MANAGE_VISITS')
    const menuItems = filter([
      {
        path: `${ROOT}/dashboard`,
        label: 'Dashboard',
      },
      {
        path: `${ROOT}/colleges`,
        label: 'School Profile',
        subItems: filter([
          {
            path: `${ROOT}/colleges/${scid}`,
            label: 'My College',
          },
          {
            path: `${ROOT}/colleges/search`,
            label: 'Search',
          },
        ]),
      },
      {
        path: `${ROOT}/application`,
        label: 'Application',
        subItems: filter([
          canViewApplicants && {
            path: `${ROOT}/application/applicants`,
            label: 'Applicants',
          },
          {
            path: `${ROOT}/application/rounds`,
            label: 'Set Rounds',
          },
          canConfigureApplicationIntegration && {
            path: `${ROOT}/application/settings`,
            label: 'Configure',
          },
          canActivateApplications && {
            path: `${ROOT}/application/integration`,
            label: 'Activate',
          },
          canViewAppExtensions && {
            path: `${ROOT}/application/extensions`,
            label: 'Extensions',
          },
        ]),
      },
      canRecruit && {
        path: `${ROOT}/recruit`,
        label: 'High Schools',
        subItems: filter([
          {
            path: `${ROOT}/recruit/highschools`,
            label: 'Explore',
          },
          {
            path: `${ROOT}/recruit/visits`,
            label: 'Calendar',
          },
          canManageVisits && {
            path: `${ROOT}/recruit/manage-visits`,
            label: 'Manage Visits',
          },
        ]),
      },
      onlyIfSubItemsExist({
        label: 'Engage',
        path: `${ROOT}/engage`,
        subItems: filter([
          {
            path: `${ROOT}/engage/presence`,
            label: 'Presence',
          },
          {
            path: `${ROOT}/engage/messaging`,
            label: 'Messaging',
          },
          supportsCrmIntegration && {
            path: `${ROOT}/engage/integrations`,
            label: 'Integrations',
          },
        ]),
      }),
      {
        path: `${ROOT}/documents`,
        label: 'Documents',
        subItems: filter([
          canAdmissions && {
            path: `${ROOT}/documents/batches/daily`,
            label: 'Batches',
          },
          canAdmissions && {
            path: `${ROOT}/documents/batches/applicants`,
            label: 'Historical Applicants',
          },
          canAdmissions &&
            canConfigureSFTP && {
              path: `${ROOT}/documents/integrations`,
              label: 'SFTP Configuration',
            },
        ]),
      },
      handraisersEnabled && {
        path: `${ROOT}/handraisers`,
        label: 'Handraisers',
        subItems: filter([
          canViewHandraisers && {
            path: `${ROOT}/handraisers/files`,
            label: 'Files',
          },
          canViewHandraisers &&
            canConfigureSFTP && {
              path: `${ROOT}/handraisers/integrations`,
              label: 'SFTP Configuration',
            },
        ]),
      },
    ])
    const settingsItems = filter([
      {
        path: `${ROOT}/settings/profile`,
        label: 'Settings',
      },
      canManage && {
        path: `${ROOT}/org/users`,
        label: 'Users',
      },
      {
        label: 'Support',
        onClick: () => {
          window.open('https://scoir.helpdocs.io/category/j5gu4xhb9p-college-admissions')
        },
      },
      {
        onClick: () => {
          api.get('/api/logout').finally(() => {
            updateFilters({})
            clearSignedInApp('admissions')
            removeBearerToken()
            userPilotHelper.logout()
            window.location.assign(`/logout?userType=${head(user.UserTypes)}`)
            realtimeLogout()
          })
        },
        label: 'Sign out',
      },
    ])
    return (
      <HeightObserver heightId={TOP_NAVIGATION_HEIGHT_ID}>
        <HeightTracker id={TOP_NAVIGATION_HEIGHT_ID}>
          <HeaderNavigation.TieredNavigation
            scoirLogoDestination={`${ROOT}/home`}
            organizationName={collegeName}
            userAvatarUrl={user.AvatarUrl && imageAsFullImageSrc(user.AvatarUrl)}
            messagingComponent={<DirectMessaging />}
            inboxComponent={
              <NavLink
                to={{
                  pathname: `${ROOT}/message-center/notifications`,
                }}
              >
                <HeaderNavigation.TieredNavigation.NotificationsIndicator
                  realtimeUpLinkFn={(cb) => realtimeConnect(`${user.Id}-Notifications`, cb)}
                  onFetchUnreadCount={() => api.get('/api/inbox?start=0&pageSize=1').then((inbox) => inbox.UnreadCount)}
                />
              </NavLink>
            }
            menuItems={menuItems}
            settingsItems={settingsItems}
          />
        </HeightTracker>
      </HeightObserver>
    )
  }
}

const RealtimeMessagingConnected = withContext({
  logout: 'realtimeLogout',
  connect: 'realtimeConnect',
})(AdmissionsHeaderNavigation)

const ConnectedHeaderNavigation = connect(
  (store) => ({
    user: store.user.record,
    scid: get(store.college.record, 'Scid'),
    documentDelegateName: get(store.college.record, 'Integrations.DocumentDelegateName', ''),
    supportsCrmIntegration: !!get(store, `college.record.Features.${features.CRM_INTEGRATION}`),
    applicationIntegrationsEnabled: !!get(store, `college.record.Features.${features.APPLICATION_INTEGRATION}`),
    applicationActivationEnabled: !!get(store, `college.record.Features.${features.APPLICATION_ACTIVATION}`),
    scoirApplyEnabled: !!get(store, `college.record.ScoirApplyEnabled`),
    isShadow: get(store.user.record, 'IsShadow', false),
    handraisersEnabled: isHandraisersEnabled(store),
    supportsHandraisers: isFeatureEnabled(store, collegeFeatures.SUPPORTS_HANDRAISERS),
  }),
  {
    updateFilters: mapFilterActions.updateFilters,
  },
)(RealtimeMessagingConnected)

const HeaderNavigationWrapper = (props) => {
  const collegeName = useCollegeName()
  return (
    <ConnectedHeaderNavigation
      {...props}
      collegeName={collegeName}
    />
  )
}

export default HeaderNavigationWrapper
